<template>
    <Modal
        id="create-buy-request-modal"
        ref="create-buy-request-modal"
        v-model:open="open"
        title="create_buy_request"
        width="xl"
        @cancel="cancel"
    >
        <SelectField
            v-model="modelValue.buyer_id"
            :options="accounts"
            name="account_title"
            value="account_uid"
            label="account_title"
            :required="true"
            :on-search="loadSelectAccountOptions"
        />
        <SelectField
            v-model="modelValue.asset_id"
            name="asset_title"
            value="asset_uid"
            label="asset_title"
            :options="assets"
            :required="true"
        />
        <NumberField
            v-model="modelValue.desired_quantity"
            :label="$t('quantity')"
            name="quantity"
            :required="true"
            :min="1"
        />
        <NumberField
            v-model="modelValue.target_price_per_unit"
            :label="$t('price_per_unit')"
            name="price_per_unit"
            :required="true"
            :min="0"
        />
        <Datepicker
            v-model="modelValue.valid_until"
            name="valid_until"
            :label="$t('valid_to')"
            :min-date="minDate"
            :max-date="maxDate"
        />
        <TextField
            v-model="modelValue.note"
            :label="$t('note')"
            name="note"
            :rows="3"
        />
        <template #actions>
            <button
                class="btn bg-primary-accent-500 text-white"
                @click="submit"
            >
                {{ $t('save') }}
            </button>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import {
    Datepicker,
    Modal,
    NumberField,
    SelectField,
    TextField,
} from '@src/components'
import { Account, BuyRequest } from '@src/types'
import { useVModels } from '@vueuse/core'
import moment from 'moment'
import { onMounted, ref } from 'vue'
import { getSmAssets } from '@utils/api/secondaryMarket'
import { getAccounts } from '@utils/api/accounts'

const props = defineProps<{
    open: boolean
    modelValue: BuyRequest
    platformName?: string | null
}>()

const accounts = ref<{ label: string | null; value: number | string | null }[]>(
    []
)
const assets = ref<{ label: string; value: string | null }[]>([])

const minDate = moment().toDate()
const maxDate = moment().add(30, 'days').toDate()

const emit = defineEmits([
    'update:modelValue',
    'update:open',
    'cancel',
    'submit',
])

const { modelValue, open } = useVModels(props, emit)

function cancel() {
    emit('cancel')
}

function submit() {
    emit('submit')
}

// Use platformName prop directly in the account search API request
async function loadSelectAccountOptions(search: string) {
    console.log('props.platformName', props.platformName)

    const accountsSearchRequest = await getAccounts({
        filter: {
            search: search,
            platform_name_exact: props.platformName,
        },
    })

    accounts.value = accountsSearchRequest.data.accounts.map(
        (account: Account) => {
            return {
                label: account.account_title,
                value: account.account_uid as string,
            }
        }
    )
}

onMounted(async () => {
    if (!modelValue.value.valid_until) {
        modelValue.value.valid_until = moment()
            .add(30, 'days')
            .format('YYYY-MM-DD HH:mm:ss')
    }

    if (props.platformName) {
        const assetsRequest = await getSmAssets(props.platformName)
        assets.value = assetsRequest.data.data.assets
    }
})
</script>
